import { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    try {
      // Attempt to sign out from Supabase (local device scope is enough for web apps)
      const { error } = await supabase.auth.signOut({ scope: 'local' });
      if (error) {
        console.error('Error signing out:', error);
      }
    } catch (error) {
      console.error('Exception during signOut:', error);
    } finally {
      // HARD CLEAR any persisted auth in case the network call failed
      try {
        const PROJECT_REF = 'mqzjjyqipuaoagcqrrer';
        const KEY_PREFIX = `sb-${PROJECT_REF}`;
        Object.keys(localStorage).forEach((k) => {
          if (k.startsWith('sb-') || k.includes(PROJECT_REF) || k.includes('supabase')) {
            localStorage.removeItem(k);
          }
        });
        // Explicitly remove the known auth token key name
        localStorage.removeItem(`${KEY_PREFIX}-auth-token`);
      } catch (e) {
        console.warn('Failed to clear localStorage keys:', e);
      }

      // Clear in-memory auth state
      setSession(null);
      setUser(null);

      // Replace location to prevent back button restoring previous state
      window.location.replace('/');
    }
  };

  const value = {
    user,
    session,
    loading,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};